// ─────────────────────────────────────────────────────────────────────────────
// CopyButton — copies `value` to clipboard, shows a "Copied" toast briefly.
//
// Used in the pending state for the VA number. Kept as a reusable component
// because future surfaces (reference display, transaction ID echo, etc.)
// will need the same affordance.
//
// Failure handling:
//   Clipboard API can fail in several ways — insecure context (http://),
//   iframe without permission, browser denies. On failure we fall back to
//   selecting the nearby value so the user can cmd/ctrl-C manually.
//   No error toast shown — selection is itself the feedback.
//
// Uses navigator.clipboard.writeText which works in all browsers Posteering
// targets (Chrome 66+, Safari 13.1+, Firefox 63+). Requires a secure
// context (https://) — which production checkout.posteering.com is by
// definition, and localhost counts as secure for dev.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback } from 'react';

export interface CopyButtonProps {
  /** The string to copy to clipboard when clicked. */
  value: string;
  /** Accessibility label for the button; describes what's being copied. */
  ariaLabel: string;
  /** How long to show the "Copied" toast, in ms. Default 1400. */
  toastDurationMs?: number;
}

export function CopyButton({
  value,
  ariaLabel,
  toastDurationMs = 1400,
}: CopyButtonProps) {
  const [copied, setCopied] = useState<boolean>(false);

  const handleClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), toastDurationMs);
    } catch {
      // Clipboard API denied. Fall back to document-level selection of the
      // value wherever it appears in the DOM. Text selection is its own
      // feedback — the user sees the VA number highlighted and knows to
      // cmd/ctrl-C manually. We don't change visual state here because
      // the user didn't actually copy anything yet.
      selectTextByContent(value);
    }
  }, [value, toastDurationMs]);

  return (
    <button
      type="button"
      className={copied ? 'copy-btn copied' : 'copy-btn'}
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
      <span className="copy-toast" aria-hidden="true">
        Copied
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fallback for when navigator.clipboard is unavailable: find a DOM node
// whose textContent exactly matches `value` and select its contents so the
// user can copy with the keyboard.
//
// Walks only text nodes via TreeWalker (cheap, ~1ms for our DOM size).
// Stops at the first match. If nothing matches, silently does nothing —
// we're already in the failure path, nothing more to do.
// ─────────────────────────────────────────────────────────────────────────────

function selectTextByContent(value: string): void {
  if (typeof document === 'undefined') return;
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    null
  );

  let node: Node | null = walker.nextNode();
  while (node) {
    if (node.textContent?.trim() === value) {
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      return;
    }
    node = walker.nextNode();
  }
}
